import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AdapterFindInterface } from '@zaoblako/shared/modules/traffic/dtos/adapter/adapter-find.dto';
import { AdapterFormDto, AdapterFormInterface } from '@zaoblako/shared/modules/traffic/dtos/adapter/adapter-form.dto';
import { SensorFindInterface } from '@zaoblako/shared/modules/traffic/dtos/sensor/sensor-find.dto';
import { AdapterTypeEnum } from '@zaoblako/shared/modules/traffic/enums/adapter.type.enum';
import { SensorTypeEnum } from '@zaoblako/shared/modules/traffic/enums/sensor.type.enum';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { TrafficAdapterService } from '@zaoblako/web/core/services/traffic/traffic-adapter.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { RoomSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/room-single-select/room-single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { TableComponent } from '@zaoblako/web/shared/dumbs/table/table.component';
import { AdapterTypeNamePipe } from '@zaoblako/web/shared/pipes/adapter-type-name.pipe';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { SensorTypeNamePipe } from '@zaoblako/web/shared/pipes/sensor-type-name.pipe';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { SelectButtonModule } from 'primeng/selectbutton';
import { Observable } from 'rxjs';

@Component({
  selector: 'crm-traffic-adapter-form',
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent,
    ButtonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    SelectButtonModule,
    FormsModule,
    RoomSingleSelectComponent,
    TableComponent
  ],
  templateUrl: './traffic-adapter-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrafficAdapterFormComponent extends BaseFormComponent<AdapterFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public engine: 'class-transformer' | 'serde' = 'serde';
  public dto = AdapterFormDto;
  public adapter: AdapterFormInterface = <AdapterFormInterface>{};

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public readonly adapterTypeOptions: { label: string; value: AdapterTypeEnum }[] = Object.values(AdapterTypeEnum).map(
    (value) => ({
      label: new AdapterTypeNamePipe().transform(value),
      value: value
    })
  );

  public readonly sensorTypeOptions: { label: string; value: SensorTypeEnum }[] = Object.values(SensorTypeEnum).map(
    (value) => ({
      label: new SensorTypeNamePipe().transform(value),
      value: value
    })
  );

  public sensorColumns: CrmTableColumn[] = [
    { label: 'Название' },
    { label: 'Направление', class: 'w-19rem' },
    { class: 'w-3rem' }
  ];

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly trafficAdapterService: TrafficAdapterService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly confirmService: ConfirmDialogService,
    private readonly errorService: ErrorService,
    private readonly router: Router
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.trafficAdapterService
      .findById<AdapterFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (adapter) => {
          this.adapter = adapter;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveAdapter() {
    if (this.adapter.sensors?.length && this.adapter.sensors.some((sensor) => !sensor.name?.length || !sensor.type)) {
      return this.errorService.addCustomError('Не заполнены все поля в разделе "Сенсоры"');
    }

    if (this.checkValidate(this.adapter).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.trafficAdapterService.update<AdapterFormInterface, AdapterFindInterface>(this.adapter).subscribe({
          next: (adapter) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Оборудование "${this.adapter.name}" обновлено`);
            this.formStoreService.saveNext(adapter, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.trafficAdapterService.create<AdapterFindInterface>(this.adapter).subscribe({
          next: (adapter) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Оборудование создано');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/traffic/equipment/card/' + adapter.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(adapter, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteAdapter() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить оборудование "${this.adapter.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.trafficAdapterService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Оборудование "${this.adapter.name}" удалено`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public addSensor() {
    if (!Array.isArray(this.adapter.sensors)) {
      this.adapter.sensors = [];
    }

    this.adapter.sensors = this.adapter.sensors.concat(<SensorFindInterface>{});
  }

  public deleteSensor(sensor: SensorFindInterface) {
    this.adapter.sensors = this.adapter.sensors.filter((_sensor) => _sensor !== sensor);
  }

  public toSensor(sensor: any): SensorFindInterface {
    return sensor as SensorFindInterface;
  }
}
